import type { CoinFountainTuning, FountainEmission, NumberRange } from "./CoinFountainTuning";

/** One frame's geometry in the fountain's space: where coins are thrown from, how big they are, where they fly. */
export interface FountainArea {
  /** The emitter point. */
  readonly originX: number;
  readonly originY: number;
  /** The anchor's scale: the spawn box and the coins are sized by it. */
  readonly scale: number;
  /** The visible rectangle. */
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** A coin in flight. `slot` is its place in the pool (0 … `maxCoins` − 1), the same for its whole flight. */
export interface FountainParticle {
  readonly slot: number;
  readonly x: number;
  readonly y: number;
  /** Radians. */
  readonly rotation: number;
  readonly scale: number;
  /** Its own spin, times its art's speed. */
  readonly spinRate: number;
  /** Where in its spin loop it starts, 0–1. */
  readonly spinPhase: number;
}

/** Told when a coin is thrown and when it leaves (out of sight, or `clear`). */
export interface FountainEmitterListener {
  spawned(particle: FountainParticle): void;
  despawned(particle: FountainParticle): void;
}

/** A pooled coin's flight state; outside the emitter it is read as a `FountainParticle`. */
interface Flight {
  readonly slot: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  /** Radians per second. */
  turn: number;
  scale: number;
  spinRate: number;
  spinPhase: number;
}

/**
 * A coin fountain's motion, with no renderer: it throws coins on the emission it was given (a burst, then a rate),
 * flies them under gravity and lets each one go once it is out of sight past the bottom or a side. A coin is aimed,
 * not just launched: it rises to a share of the room above the emitter and falls back to the emitter's height a share
 * of the half-width out, so the fountain fills any screen shape the same way. Pooled (`maxCoins` flights, nothing
 * allocated per coin); the randomness is injected, so a test can replay it.
 */
export class FountainEmitter {
  private readonly tuning: CoinFountainTuning;
  private readonly random: () => number;
  private readonly listener: FountainEmitterListener;
  /** Free flights; `pop` hands out the lowest slot first. */
  private readonly free: Flight[];
  private readonly live: Flight[] = [];
  private emission: FountainEmission | null = null;
  private pendingBurst = 0;
  /** The fraction of a coin the rate has owed since the last whole one. */
  private carry = 0;

  public constructor(input: { tuning: CoinFountainTuning; random: () => number; listener: FountainEmitterListener }) {
    this.tuning = input.tuning;
    this.random = input.random;
    this.listener = input.listener;
    this.free = Array.from({ length: input.tuning.maxCoins }, (_, i): Flight => {
      const slot = input.tuning.maxCoins - 1 - i;
      return { slot, x: 0, y: 0, vx: 0, vy: 0, rotation: 0, turn: 0, scale: 1, spinRate: 1, spinPhase: 0 };
    });
  }

  /** The coins in flight. */
  public get particles(): readonly FountainParticle[] {
    return this.live;
  }

  /** Nothing to throw and nothing in flight. */
  public get isIdle(): boolean {
    return this.emission === null && this.live.length === 0;
  }

  /**
   * Run at `emission`: start throwing, or switch to it — its burst on the next update (the largest asked for in the
   * meantime wins), then its rate.
   */
  public run(emission: FountainEmission): void {
    this.emission = emission;
    this.pendingBurst = Math.max(this.pendingBurst, emission.burst);
  }

  /** Throw nothing more; the coins in flight finish their arcs. */
  public stopThrowing(): void {
    this.emission = null;
    this.pendingBurst = 0;
    this.carry = 0;
  }

  /** Every coin out at once, and nothing more thrown. */
  public clear(): void {
    this.stopThrowing();
    while (this.live.length > 0) this.release(this.live.length - 1);
  }

  /** Advance `seconds`: fly the coins, let go of the ones out of sight, then throw what the emission asks for. */
  public update(seconds: number, area: FountainArea): void {
    this.fly(seconds, area);
    if (this.emission) this.throwCoins(seconds, this.emission, area);
  }

  private fly(seconds: number, area: FountainArea): void {
    const { gravity, cullMargin } = this.tuning;
    // Backwards, so a coin let go (swapped with the last) is never visited twice.
    for (let i = this.live.length - 1; i >= 0; i--) {
      const flight = this.live[i];
      flight.vy += gravity * seconds;
      flight.x += flight.vx * seconds;
      flight.y += flight.vy * seconds;
      flight.rotation += flight.turn * seconds;
      if (flight.y > area.bottom + cullMargin || flight.x < area.left - cullMargin || flight.x > area.right + cullMargin) this.release(i);
    }
  }

  private throwCoins(seconds: number, emission: FountainEmission, area: FountainArea): void {
    this.carry += emission.ratePerSecond * seconds;
    const steady = Math.floor(this.carry);
    this.carry -= steady;
    const count = Math.min(this.free.length, this.pendingBurst + steady);
    this.pendingBurst = 0;
    for (let i = 0; i < count; i++) this.launch(this.free.pop()!, emission.heightScale, area);
  }

  private launch(flight: Flight, heightScale: number, area: FountainArea): void {
    const { tuning, random } = this;
    const rise = pick(tuning.apexHeight, random) * heightScale * Math.max(0, area.originY - area.top);
    flight.vy = -Math.sqrt(2 * tuning.gravity * rise);
    // Up and back down to the emitter's height; the side speed covers the reach in that time.
    const airborneSeconds = (-2 * flight.vy) / tuning.gravity;
    const side = random() < 0.5 ? -1 : 1;
    const reach = (pick(tuning.sideReach, random) * (area.right - area.left)) / 2;
    flight.vx = airborneSeconds > 0 ? (side * reach) / airborneSeconds : 0;
    flight.x = area.originX + either(tuning.spawnHalfSize.width, random) * area.scale;
    flight.y = area.originY + either(tuning.spawnHalfSize.height, random) * area.scale;
    flight.rotation = toRadians(either(tuning.coin.tiltDegrees, random));
    flight.turn = toRadians(either(tuning.coin.turnDegreesPerSecond, random));
    flight.scale = pick(tuning.coin.scale, random) * area.scale;
    flight.spinRate = pick(tuning.coin.spinRate, random);
    flight.spinPhase = random();
    this.live.push(flight);
    this.listener.spawned(flight);
  }

  /** Let go of the coin at `index` in `live` (the last one takes its place). */
  private release(index: number): void {
    const flight = this.live[index];
    const last = this.live.pop()!;
    if (index < this.live.length) this.live[index] = last;
    this.free.push(flight);
    this.listener.despawned(flight);
  }
}

function pick(range: NumberRange, random: () => number): number {
  return range.min + (range.max - range.min) * random();
}

/** Between −`extent` and `extent`. */
function either(extent: number, random: () => number): number {
  return (random() * 2 - 1) * extent;
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}
